'use client'

import { useMemo, useState } from 'react'

import { round } from 'lodash-es'
import { Cell, Label, Legend, Pie, PieChart, ResponsiveContainer, Tooltip, TooltipProps } from 'recharts'
import { ContentType } from 'recharts/types/component/DefaultLegendContent'
import { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent'
import { ContentType as LabelContentType } from 'recharts/types/component/Label'
import { PolarViewBox } from 'recharts/types/util/types'
import { theme } from 'tailwind.config.js'

import { DailyRequestHealthEmpty } from 'components/dashboard/analytics-overview/DailyRequestHealthChart/DailyRequestHealthEmpty'
import { DailyRequestHealthSkeleton } from 'components/dashboard/analytics-overview/DailyRequestHealthChart/DailyRequestHealthSkeleton'
import { TooltipContent } from 'components/dashboard/analytics-overview/DailyRequestHealthChart/TooltipContent'

import { useRequestHealth } from 'hooks/useRequestHealth'

import { getFormattedUsage } from 'lib/usage/getTotalUsage'

export const INNER_RADIUS = 45
export const OUTER_RADIUS = 60

const green = theme.extend.colors.success.default.DEFAULT
const red = theme.extend.colors.error.default.DEFAULT

const COLORS = [green, red]

export const DailyRequestHealthChart = () => {
  const [focusSeries, setFocusSeries] = useState<string | null>(null)

  const { data, dataTyped, isLoading } = useRequestHealth()

  const dataSerialized = dataTyped?.reduce(
    (acc, { failures, success, ...rest }) => {
      acc[0].value += success
      acc[1].value += failures
      acc[2].value += failures + success
      acc[3].value += rest['400s']
      acc[4].value += rest['500s']
      return acc
    },
    [
      { name: 'success', value: 0 },
      { name: 'failed', value: 0 },
      { name: 'total', value: 0 },
      { name: '400', value: 0 },
      { name: '500', value: 0 }
    ]
  )

  const total = dataSerialized ? getFormattedUsage(dataSerialized[2].value) : 0

  const renderCustomLabel: LabelContentType = ({ viewBox }) => {
    const { cx, cy } = viewBox as PolarViewBox
    return (
      <text data-testid='daily-request-health-total' dominantBaseline='central' textAnchor='middle' x={cx} y={cy}>
        <tspan className='fill-text-default text-2xl font-bold' dy='-0.5rem' x='50%'>
          {total}
        </tspan>
        <tspan className=' fill-text-alternative' dy='2rem' x='50%'>
          Total
        </tspan>
      </text>
    )
  }

  const renderLegend: ContentType = ({ payload }) => {
    const bgClassNames = ['bg-success-default', 'bg-error-default'].map(bg => `h-4 w-4 ${bg} rounded-sm`)

    const percentFormatted = (percent: number) => `(${round(percent * 100, 2)}%)`

    return (
      <div className='border-border-muted mx-auto flex max-w-[340px] flex-wrap justify-between gap-2 rounded-[5px] border px-4 py-2'>
        {payload?.map((entry, index) => {
          return (
            <div className='flex items-center gap-1' key={`item-${entry.value}`}>
              <div className={bgClassNames[index]} />
              <div className='text-text-default capitalize'>{entry.value}</div>

              <div className='text-text-default' data-testid={`daily-request-health-${entry.value}`}>
                {
                  // The typing of entry.payload is incorrect in the recharts library
                  percentFormatted((entry.payload as unknown as { percent: number }).percent)
                }
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  const CustomTooltip = useMemo(
    () =>
      // eslint-disable-next-line react/display-name
      ({ active, payload }: TooltipProps<ValueType, NameType>) => {
        if (active && payload?.length && dataSerialized) {
          return <TooltipContent dataSerialized={dataSerialized} payload={payload} />
        }

        return null
      },
    [dataSerialized]
  )

  if (isLoading) {
    return <DailyRequestHealthSkeleton />
  }

  if (!data?.data[0].result?.length) {
    return <DailyRequestHealthEmpty />
  }

  return (
    <div className='xs:h-[220px] h-60'>
      <ResponsiveContainer>
        <PieChart>
          <Pie
            data={dataSerialized ? dataSerialized.slice(0, 2) : []}
            dataKey='value'
            endAngle={450}
            fill={red}
            innerRadius={INNER_RADIUS}
            outerRadius={OUTER_RADIUS}
            startAngle={-90}
            stroke='none'
          >
            {dataSerialized?.map((entry, index) => (
              <Cell
                fill={COLORS[index]}
                key={`cell-${index}`}
                onMouseEnter={() => setFocusSeries(entry.name)}
                onMouseLeave={() => setFocusSeries(null)}
                opacity={focusSeries && focusSeries !== entry.name ? 0.25 : 1}
              />
            ))}
            <Label content={renderCustomLabel} position='center' />
          </Pie>
          <Legend content={renderLegend} verticalAlign='bottom' />
          <Tooltip
            content={<CustomTooltip />}
            cursor={false}
            isAnimationActive={false}
            offset={30}
            position={{ y: -10 }}
          />
        </PieChart>
      </ResponsiveContainer>
    </div>
  )
}
